import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  Divider,
  IconButton,
  Collapse,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Flight as FlightIcon,
  DirectionsCar as CarIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import {
  combineDateTime,
  formatFlightDateTime,
  validateFutureDateTime,
  getUserTimezone,
  getTodayDateString,
  parseLegacyDateTime
} from '../utils/dateUtils';

interface TransportationRequest {
  id: string;
  requestType: 'airport_to_hotel' | 'hotel_to_airport';
  airline: string;
  flightNumber: string;
  flightDate: string;
  flightTime: string;
  passengerCount: number;
  specialRequirements?: string;
  pickupLocation?: string;
  status: 'pending' | 'approved' | 'confirmed' | 'denied' | 'cancelled';
  adminNotes?: string;
  createdAt: string;
  details?: {
    providerName: string;
    vehicleType: string;
    driverName?: string;
    driverPhone?: string;
    pickupTime: string;
    vehicleInfo?: string;
    confirmationNumber?: string;
  };
}

interface TransportationManagerProps {
  guestId: string;
}

export const TransportationManager: React.FC<TransportationManagerProps> = ({ guestId }) => {
  const [requests, setRequests] = useState<TransportationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>('');
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  const [formValidationError, setFormValidationError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    requestType: 'airport_to_hotel' as 'airport_to_hotel' | 'hotel_to_airport',
    airline: '',
    flightNumber: '',
    flightDate: '',
    flightTime: '',
    passengerCount: 1,
    specialRequirements: '',
    pickupLocation: ''
  });

  useEffect(() => {
    loadTransportationRequests();
    // Detect user's timezone
    setUserTimezone(getUserTimezone());
  }, []);

  const loadTransportationRequests = async () => {
    try {
      setLoading(true);
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/api/transportation/guests/transportation`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load transportation requests');
      }

      const data = await response.json();
      setRequests(data.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    try {
      // Clear any previous validation errors
      setFormValidationError(null);
      setDateValidationError(null);

      // Validate flight number format (should be airline code + numbers, e.g., AA123, JBU1763)
      const flightNumberPattern = /^[A-Z]{2,3}\d{1,4}$/i;
      if (!flightNumberPattern.test(formData.flightNumber.trim())) {
        setFormValidationError('Flight number must be in format: Airline Code + Number (e.g., AA123, JBU1763, DL456)');
        return;
      }

      // Validate that date/time is in the future
      if (!validateFutureDateTime(formData.flightDate, formData.flightTime, 1)) {
        setDateValidationError('Flight date and time must be in the future');
        return;
      }

      // Combine date and time into ISO datetime string
      const flightDateTime = combineDateTime(formData.flightDate, formData.flightTime);

      // Prepare request payload with both new and legacy formats for backward compatibility
      const requestPayload = {
        ...formData,
        flightDateTime, // New ISO format
        timezone: userTimezone, // Include timezone information
        // Keep legacy fields for backward compatibility
        flightDate: formData.flightDate,
        flightTime: formData.flightTime
      };

      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/api/transportation/guests/transportation/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to submit request');
      }

      setOpenDialog(false);
      setFormData({
        requestType: 'airport_to_hotel',
        airline: '',
        flightNumber: '',
        flightDate: '',
        flightTime: '',
        passengerCount: 1,
        specialRequirements: '',
        pickupLocation: ''
      });
      setDateValidationError(null);
      setFormValidationError(null);
      
      await loadTransportationRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDateTimeChange = (field: 'flightDate' | 'flightTime', value: string) => {
    const updatedFormData = { ...formData, [field]: value };
    setFormData(updatedFormData);

    // Validate if both date and time are filled
    if (updatedFormData.flightDate && updatedFormData.flightTime) {
      if (!validateFutureDateTime(updatedFormData.flightDate, updatedFormData.flightTime, 1)) {
        setDateValidationError('Flight date and time must be in the future');
      } else {
        setDateValidationError(null);
      }
    } else {
      setDateValidationError(null);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/api/transportation/guests/transportation/${requestId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel request');
      }

      await loadTransportationRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to delete this cancelled request? This action cannot be undone.')) {
      return;
    }

    try {
      const apiUrl = 'https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(`${apiUrl}/api/transportation/guests/transportation/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete request');
      }

      await loadTransportationRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'confirmed': return 'success';
      case 'denied': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (requestType: string) => {
    return requestType === 'airport_to_hotel' ? <FlightIcon /> : <CarIcon />;
  };

  /**
   * Format flight date and time for display
   * Handles both new ISO format (flightDateTime) and legacy format (flightDate + flightTime)
   */
  const formatFlightInfo = (request: TransportationRequest): {
    date: string;
    time: string;
    timezone: string;
  } => {
    try {
      // Check if request has new ISO format
      if ((request as any).flightDateTime) {
        return formatFlightDateTime((request as any).flightDateTime);
      }
      // Handle legacy format
      else if (request.flightDate && request.flightTime) {
        return parseLegacyDateTime(request.flightDate, request.flightTime);
      }
      return { date: 'Invalid date', time: '', timezone: '' };
    } catch (error) {
      console.error('Error formatting flight info:', error);
      return { date: 'Invalid date', time: '', timezone: '' };
    }
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Typography>Loading transportation requests...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          🚗 Transportation
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Request Transportation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Transportation Requests
            </Typography>
            <Typography color="text.secondary" paragraph>
              Request transportation between the airport and hotel for your convenience.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Request Transportation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {requests.map((request) => (
            <Grid item xs={12} key={request.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(request.requestType)}
                      <Typography variant="h6">
                        {request.requestType === 'airport_to_hotel' ? 'Airport → Hotel' : 'Hotel → Airport'}
                      </Typography>
                    </Box>
                    <Chip
                      label={request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      color={getStatusColor(request.status) as any}
                      size="small"
                    />
                  </Box>

                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Airline
                      </Typography>
                      <Typography variant="body1">
                        {request.airline}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Flight Number
                      </Typography>
                      <Typography variant="body1">
                        {request.flightNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Flight Date
                      </Typography>
                      <Typography variant="body1">
                        {formatFlightInfo(request).date}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Flight Time
                      </Typography>
                      <Typography variant="body1">
                        {formatFlightInfo(request).time} {formatFlightInfo(request).timezone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Passengers
                      </Typography>
                      <Typography variant="body1">
                        {request.passengerCount}
                      </Typography>
                    </Grid>
                  </Grid>

                  {request.status === 'confirmed' && request.details && (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom color="success.main">
                        ✅ Transportation Confirmed
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Provider
                          </Typography>
                          <Typography variant="body1">
                            {request.details.providerName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Vehicle Type
                          </Typography>
                          <Typography variant="body1">
                            {request.details.vehicleType}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Pickup Time
                          </Typography>
                          <Typography variant="body1">
                            {formatTime(request.details.pickupTime)}
                          </Typography>
                        </Grid>
                        {request.details.driverName && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Driver
                            </Typography>
                            <Typography variant="body1">
                              <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              {request.details.driverName}
                              {request.details.driverPhone && (
                                <Box component="span" ml={1}>
                                  <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                  {request.details.driverPhone}
                                </Box>
                              )}
                            </Typography>
                          </Grid>
                        )}
                        {request.details.vehicleInfo && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Vehicle Details
                            </Typography>
                            <Typography variant="body1">
                              {request.details.vehicleInfo}
                            </Typography>
                          </Grid>
                        )}
                        {request.details.confirmationNumber && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Confirmation Number
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {request.details.confirmationNumber}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {request.status === 'approved' && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Your transportation request has been approved! You will receive confirmation details soon.
                    </Alert>
                  )}

                  {request.status === 'denied' && request.adminNotes && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Request denied: {request.adminNotes}
                    </Alert>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <IconButton
                      onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                      size="small"
                    >
                      {expandedRequest === request.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      <Typography variant="body2" ml={0.5}>
                        {expandedRequest === request.id ? 'Less Details' : 'More Details'}
                      </Typography>
                    </IconButton>

                    {(request.status === 'pending' || request.status === 'approved') && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => handleCancelRequest(request.id)}
                      >
                        Cancel
                      </Button>
                    )}
                    
                    {request.status === 'cancelled' && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteRequest(request.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>

                  <Collapse in={expandedRequest === request.id}>
                    <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                      {request.specialRequirements && (
                        <Box mb={2}>
                          <Typography variant="body2" color="text.secondary">
                            Special Requirements
                          </Typography>
                          <Typography variant="body1">
                            {request.specialRequirements}
                          </Typography>
                        </Box>
                      )}
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Airline
                        </Typography>
                        <Typography variant="body1">
                          {request.airline}
                        </Typography>
                      </Box>
                      {request.pickupLocation && (
                        <Box mb={2}>
                          <Typography variant="body2" color="text.secondary">
                            Pickup Location
                          </Typography>
                          <Typography variant="body1">
                            {request.pickupLocation}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Requested on {new Date(request.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Request Transportation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Transportation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Transportation Type</InputLabel>
              <Select
                value={formData.requestType}
                onChange={(e) => setFormData({ ...formData, requestType: e.target.value as any })}
                label="Transportation Type"
              >
                <MenuItem value="airport_to_hotel">Airport → Hotel</MenuItem>
                <MenuItem value="hotel_to_airport">Hotel → Airport</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label={formData.requestType === 'airport_to_hotel' ? 'Flight - Arrival Date' : 'Flight - Departure Date'}
              type="date"
              value={formData.flightDate}
              onChange={(e) => handleDateTimeChange('flightDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getTodayDateString() }}
              error={!!dateValidationError}
              helperText={dateValidationError ? '' : 'Select your flight date'}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label={formData.requestType === 'airport_to_hotel' ? 'Flight - Arrival Time' : 'Flight - Departure Time'}
              type="time"
              value={formData.flightTime}
              onChange={(e) => handleDateTimeChange('flightTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!dateValidationError}
              helperText={
                dateValidationError 
                  ? '' 
                  : userTimezone 
                    ? `Enter time in your local timezone (${userTimezone})` 
                    : 'Enter time in your local timezone (24-hour format)'
              }
              required
            />

            {dateValidationError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {dateValidationError}
              </Alert>
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Airline"
              value={formData.airline}
              onChange={(e) => setFormData({ ...formData, airline: e.target.value })}
              placeholder="e.g., American Airlines, Delta, United"
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Flight Number"
              value={formData.flightNumber}
              onChange={(e) => {
                setFormData({ ...formData, flightNumber: e.target.value });
                setFormValidationError(null); // Clear error when user types
              }}
              placeholder="e.g., AA123, JBU1763, DL456"
              helperText="Format: Airline Code + Number (e.g., AA123, JBU1763)"
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Number of Passengers"
              type="number"
              value={formData.passengerCount}
              onChange={(e) => setFormData({ ...formData, passengerCount: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1, max: 8 }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Special Requirements (Optional)"
              multiline
              rows={2}
              value={formData.specialRequirements}
              onChange={(e) => setFormData({ ...formData, specialRequirements: e.target.value })}
              placeholder="e.g., wheelchair accessible, child seats, etc."
            />

            <TextField
              fullWidth
              margin="normal"
              label="Pickup Location (Optional)"
              value={formData.pickupLocation}
              onChange={(e) => setFormData({ ...formData, pickupLocation: e.target.value })}
              placeholder="e.g., Terminal 1, Gate A5"
            />

            {formValidationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formValidationError}
              </Alert>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Important:</strong> Transportation requests require approval and confirmation.
              </Typography>
              <Typography variant="body2" gutterBottom>
                • All requests must be submitted by November 16, 2025
              </Typography>
              <Typography variant="body2" gutterBottom>
                • All times are in your local timezone ({userTimezone || 'detected automatically'})
              </Typography>
              <Typography variant="body2">
                • If not confirmed, please make alternative arrangements
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            setDateValidationError(null);
            setFormValidationError(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleSubmitRequest} 
            variant="contained"
            disabled={!!dateValidationError || !formData.flightDate || !formData.flightTime}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};